/**
 * CreatePeriodDialog Component
 *
 * Dialog for creating a new OKR period with custom date range.
 * Enforces: only one period can be open (unlocked) at a time.
 */

import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    Typography,
    Alert,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { CalendarMonth } from '@mui/icons-material';
import { useOKRDashboardContext } from '../OKRDashboardContext';
import { okrApi } from '../../../services/okrApi';

export const CreatePeriodDialog: React.FC = () => {
    const __muiTheme = useTheme();
    const isMobile = useMediaQuery(__muiTheme.breakpoints.down("sm"));
    const { isDarkMode, data, dialogs } = useOKRDashboardContext();
    const { periods, setSuccess, setError, refetchData } = data;
    const { dialogs: dialogState, closeCreatePeriodDialog } = dialogs;

    const [name, setName] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [saving, setSaving] = useState(false);

    const hasOpenPeriod = periods.some((p: any) => !p.is_locked || p.is_locked === 0);

    const handleSave = async () => {
        if (!name.trim() || !startDate || !endDate || hasOpenPeriod) return;
        if (new Date(startDate) >= new Date(endDate)) {
            setError('Start date must be before end date');
            return;
        }

        try {
            setSaving(true);
            await okrApi.createPeriod(name.trim(), startDate, endDate);
            setSuccess('Period created successfully');
            handleClose();
            refetchData();
        } catch (err: any) {
            setError(err.message || 'Failed to create period');
        } finally {
            setSaving(false);
        }
    };

    const handleClose = () => {
        setName('');
        setStartDate('');
        setEndDate('');
        closeCreatePeriodDialog();
    };

    return (
        <Dialog
            open={dialogState.createPeriodDialog}
            onClose={handleClose}
            fullScreen={isMobile}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    bgcolor: isDarkMode ? '#1e293b' : '#ffffff',
                    borderRadius: 3,
                },
            }}
        >
            <DialogTitle sx={{ pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                        sx={{
                            width: 44,
                            height: 44,
                            borderRadius: 2,
                            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <CalendarMonth sx={{ color: '#fff', fontSize: 24 }} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Create Period
                    </Typography>
                </Box>
            </DialogTitle>
            <DialogContent>
                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    {hasOpenPeriod && (
                        <Alert severity="warning" sx={{ borderRadius: 2 }}>
                            Lock the current open period before creating a new one.
                        </Alert>
                    )}
                    <TextField
                        autoFocus
                        label="Period Name"
                        fullWidth
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="e.g. Q1 2026, Sprint Jan-Feb, H1 2026"
                        disabled={hasOpenPeriod}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                            },
                        }}
                    />
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                            label="Start Date"
                            type="date"
                            fullWidth
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            disabled={hasOpenPeriod}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                },
                            }}
                        />
                        <TextField
                            label="End Date"
                            type="date"
                            fullWidth
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            disabled={hasOpenPeriod}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                },
                            }}
                        />
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2.5, pt: 1 }}>
                <Button
                    onClick={handleClose}
                    sx={{
                        color: isDarkMode ? '#94a3b8' : '#64748b',
                        fontWeight: 600,
                    }}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={!name.trim() || !startDate || !endDate || saving || hasOpenPeriod}
                    sx={{
                        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                        fontWeight: 600,
                        borderRadius: 2,
                        px: 3,
                    }}
                >
                    {saving ? 'Creating...' : 'Create Period'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CreatePeriodDialog;
